import React, { ComponentProps, FC, useEffect, useMemo, useRef, useState } from 'react';
import { OptionType, SelectV1 } from '@withjoy/joykit';
import { Toggle } from '@withjoy/joykit/components/SelectV1/styles';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

type ModifiedSelectV1 = Omit<
  ComponentProps<typeof SelectV1>,
  | 'fluid'
  | 'closeMenuOnSelect'
  | 'closeMenuOnClear'
  | 'hideSelectedOptions'
  | 'searchable'
  | 'loadingMessage'
  | 'disabled'
  | 'maxDropdownHeight'
  | 'noOptionsMessage'
  | 'options'
  | 'value'
>;

interface TimeZonePickerProps extends ModifiedSelectV1 {
  disabled?: boolean;
  value?: string;
  zIndex?: number;
}

/**
 * Filters and sorts options based on search input.
 * Prioritizes "starts with" matches over "contains" matches.
 * @internal Exported for testing only
 */
export function filterAndSortOptions(options: ReadonlyArray<OptionType>, inputValue: string, selectedOption: OptionType | null): ReadonlyArray<OptionType> {
  // Show all options if input matches current selection exactly (field is dirty but unchanged)
  if (selectedOption && inputValue === selectedOption.label) {
    return options;
  }

  if (!inputValue) {
    return options;
  }

  const searchTerm = inputValue.toLowerCase();
  const startsWithMatches: OptionType[] = [];
  const containsMatches: OptionType[] = [];

  for (const option of options) {
    const label = option.label.toLowerCase();
    if (label.startsWith(searchTerm)) {
      startsWithMatches.push(option);
    } else if (label.includes(searchTerm)) {
      containsMatches.push(option);
    }
  }

  // Return "starts with" matches first, then "contains" matches
  return [...startsWithMatches, ...containsMatches];
}

export const TimeZonePicker: FC<TimeZonePickerProps> = props => {
  const { onMenuClose, onMenuOpen, disabled, value, onChange, ...restProps } = props;

  const [options, setOptions] = useState<OptionType[]>([]);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const isLoadedRef = useRef(false);

  useEffect(() => {
    const loadData = async () => {
      if (!isLoadedRef.current) {
        // Dynamically import the constant data
        const { TIMEZONE_OPTIONS } = await import('./TimeZonePicker.constants');
        setOptions(TIMEZONE_OPTIONS);
        isLoadedRef.current = true;
      }
      if (options) {
        const option = options.find(option => option.value === value);
        if (option) {
          setSelectedOption(option);
        }
      }
    };
    loadData();
  }, [options, value]);

  const filteredSortedOptions = useMemo(() => filterAndSortOptions(options, inputValue, selectedOption), [options, inputValue, selectedOption]);

  const onOptionSelect = (option: OptionType | null) => {
    setSelectedOption(option);
    onChange?.(option);
  };

  const handleInputChange = useEventCallback((newInputValue: string) => {
    setInputValue(newInputValue);
  });

  const handleMenuOpen = useEventCallback(() => {
    setIsOpen(true);
    onMenuOpen?.();
  });

  const handleMenuClose = useEventCallback(() => {
    setIsOpen(false);
    onMenuClose?.();
  });

  return (
    <SelectV1
      {...restProps}
      options={filteredSortedOptions}
      zIndex={props.zIndex || 1}
      autoComplete="off"
      searchable={true}
      onChange={onOptionSelect}
      value={selectedOption}
      onInputChange={handleInputChange}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      disabled={disabled}
      // Filtering is handled externally via filteredSortedOptions to support custom sort order
      // (SelectV1's built-in filter is bypassed)
      optionFilter={() => true}
      iconRight={
        <Toggle
          source="optionSelect"
          $rotate={isOpen}
        />
      }
    />
  );
};

TimeZonePicker.displayName = 'TimeZonePicker';
